import { useEffect, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, Loader2, FileText } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { downloadGateGuidePdf } from "../lib/exporters";

// Build a slug to match what ReactMarkdown emits when you provide an id via remark-slug.
// We keep it simple: lowercase, replace non-alphanumerics with '-', collapse runs.
const slugify = (s) =>
  s.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function Help() {
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("");

  useEffect(() => {
    api.get("/help/manual")
      .then((r) => setMd(r.data.markdown))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  // Extract H2 headings for the TOC
  const toc = useMemo(() => {
    if (!md) return [];
    return md.split("\n")
      .filter((l) => /^##\s+/.test(l) && !/^###/.test(l))
      .map((l) => {
        const text = l.replace(/^##\s+/, "").trim();
        return { id: slugify(text), text };
      });
  }, [md]);

  // Highlight the TOC item currently in view
  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY + 140;
      let current = "";
      for (const h of toc) {
        const el = document.getElementById(h.id);
        if (el && el.offsetTop <= scrollY) current = h.id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [toc]);

  // Custom renderers: turn H2 text into an anchor target + style headings
  const components = {
    h1: ({ children }) => (
      <h1 className="text-3xl font-bold mt-2 mb-4 text-[#003147]" style={{ fontFamily: "Fraunces, serif" }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => {
      const id = slugify(String(children));
      return (
        <h2
          id={id}
          className="scroll-mt-24 text-xl font-bold mt-10 mb-3 text-[#006E9E] border-b-2 border-[#006E9E]/20 pb-2"
          style={{ fontFamily: "Fraunces, serif" }}
        >
          {children}
        </h2>
      );
    },
    h3: ({ children }) => (
      <h3 className="text-base font-bold mt-6 mb-2 text-[#003147]">{children}</h3>
    ),
    p:  ({ children }) => <p className="text-sm leading-relaxed mb-3 text-gray-800">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-outside ml-5 space-y-1 text-sm mb-3 text-gray-800">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-outside ml-5 space-y-1 text-sm mb-3 text-gray-800">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => <strong className="font-bold text-[#003147]">{children}</strong>,
    em:     ({ children }) => <em className="italic text-gray-700">{children}</em>,
    code:   ({ inline, children }) => inline
      ? <code className="bg-[#FEF3C6] text-[#7B3306] px-1 py-0.5 rounded-sm font-mono text-xs">{children}</code>
      : <pre className="bg-gray-50 border border-gray-200 p-3 my-3 overflow-x-auto text-xs"><code className="font-mono">{children}</code></pre>,
    a:      ({ href, children }) => <a href={href} className="text-[#006E9E] underline hover:text-[#003147]">{children}</a>,
    hr:     () => <hr className="my-8 border-gray-200" />,
    table:  ({ children }) => (
      <div className="overflow-x-auto my-4 border border-gray-200">
        <table className="ps-table text-xs">{children}</table>
      </div>
    ),
    th: ({ children }) => <th className="bg-gray-50">{children}</th>,
    td: ({ children }) => <td>{children}</td>,
  };

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="help-page">
      <PageHeader
        title="User Manual"
        subtitle="Quick reference for every module — gate events, washes, rentals, repairs, invoicing."
        testid="help-header"
        actions={
          <button
            className="ps-btn ps-btn-secondary"
            onClick={downloadGateGuidePdf}
            data-testid="download-gate-guide-help"
            title="Print the one-page gate-in / gate-out cheat sheet"
          >
            <FileText className="w-4 h-4" /> Gate Guide PDF
          </button>
        }
      />

      {loading ? (
        <div className="p-10 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading manual…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 p-6 sm:p-8 max-w-[1200px]">
          {/* Sticky TOC sidebar */}
          <nav
            className="lg:sticky lg:top-6 lg:self-start border border-gray-200 bg-white p-4 h-fit"
            data-testid="help-toc"
          >
            <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">
              <BookOpen className="w-3 h-3" /> Contents
            </div>
            <ol className="space-y-1 text-xs">
              {toc.map((h) => (
                <li key={h.id}>
                  <button
                    onClick={() => jumpTo(h.id)}
                    data-testid={`toc-${h.id}`}
                    className={`block w-full text-left px-2 py-1 transition-colors ${
                      active === h.id
                        ? "bg-[#006E9E]/10 text-[#003147] font-bold border-l-2 border-[#006E9E] pl-2"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {h.text}
                  </button>
                </li>
              ))}
            </ol>
          </nav>

          {/* Manual content */}
          <article className="prose-portside max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{md}</ReactMarkdown>
          </article>
        </div>
      )}
    </div>
  );
}
